//@ts-strict-ignore
// ts-strict causes a lot of errors in application-menu-base.directive and it has to be dealt together with all menu implementations
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { LMSpecService } from '@services/lm-spec.service';
import { LMContextService } from "@services/lm-context.service";

type LMWebAppMenuSpecs = { [key: string]: LMWebAppMenuSpec };
@Injectable()
export class LMWebAppService {
  private webAppMenuSpecs: LMWebAppMenuSpecs = {};

  constructor(
    private lmContextService : LMContextService,
    private lmSpecService: LMSpecService) {}


  public getWebAppMenuSpecWithUrl(webAppName: string, url: string): Observable<LMWebAppMenuSpec> {
    if (this.webAppMenuSpecs && this.webAppMenuSpecs[webAppName]) {
      return of(JSON.parse(JSON.stringify(this.webAppMenuSpecs[webAppName])));
    }

    return new Observable<LMWebAppMenuSpec>(observer => {
      this.lmSpecService.getApplicationMenuSpec(`${url}&allLevels=true`)
          .subscribe((appMenuSpec) => {
            this.webAppMenuSpecs[webAppName] = JSON.parse(JSON.stringify(appMenuSpec));
            observer.next(appMenuSpec);
            observer.complete();
          });
    });

  }
  public getWebAppMenuSpec(webAppName: string): Observable<LMWebAppMenuSpec> {
    // If webapp is in the collection, return the web spec

    if (this.webAppMenuSpecs && this.webAppMenuSpecs[webAppName]) {
      return of(JSON.parse(JSON.stringify(this.webAppMenuSpecs[webAppName])));
    }

    const webAppSpecList = this.lmContextService.getAccessibleWebApps(); // just the list of web apps!
    const webApp = webAppSpecList.find(w => w.webAppName === webAppName);
    if (!webApp?.navigationMenuUrl) {
      // throw an error?
      return of({});
    }

    return new Observable<LMWebAppMenuSpec>(observer => {
      this.lmSpecService.getApplicationMenuSpec(`${webApp.navigationMenuUrl}&allLevels=true`)
        .subscribe((appMenuSpec) => {
          this.webAppMenuSpecs[webAppName] = JSON.parse(JSON.stringify(appMenuSpec));
          observer.next(appMenuSpec);
          observer.complete();
        });
    });
  }

  public getAllCachedWebApps(): LMWebAppMenuSpec[] {
    const allCachedWebApps: LMWebAppMenuSpec[] = [];
    Object.keys(this.webAppMenuSpecs).map(k => allCachedWebApps.push(JSON.parse(JSON.stringify(this.webAppMenuSpecs[k]))));
    return allCachedWebApps;
  }

  public isPagePanelInaccessible(pagePanelSpec: LMPagePanelSpec): boolean {
    const webAppName = this.lmContextService.getWebApp();
    const webAppSpec = this.webAppMenuSpecs[webAppName];

    if (!webAppSpec) {
      return false;
    }
    if (
      webAppSpec.menuSecurityEnabled &&
        webAppSpec.inaccessibleItems !== undefined &&
        pagePanelSpec.menuSecurityId !== undefined
    ) {
      const menuName = webAppSpec.menuName;
      // need to keep in sync with SecurityHelper.buildPagePanelId()
      const propertyId =
          'pagePanel.' + menuName + '.' + pagePanelSpec.menuSecurityId;
      const homePagePropertyId = 'pagePanel.' + pagePanelSpec.menuSecurityId;
      if (webAppSpec.inaccessibleItems.includes(propertyId)) {
        return true;
      }
      return webAppSpec.inaccessibleItems.includes(homePagePropertyId);
    } else {
      return false;
    }
  }

  public isPagePaneInaccessible(pagePaneSpec: LMPagePaneSpec): boolean {
    const webAppName = this.lmContextService.getWebApp();
    const webAppSpec = this.webAppMenuSpecs[webAppName];

    if (!webAppSpec) {
      return false;
    }

    if (
      webAppSpec.menuSecurityEnabled &&
        webAppSpec.inaccessibleItems !== undefined &&
        pagePaneSpec.menuSecurityId !== undefined
    ) {
      const menuName = webAppSpec.menuName;
      // need to keep in sync with SecurityHelper.buildPagePaneId()
      const propertyId =
          'pagePanel.' + menuName + '.' + pagePaneSpec.menuSecurityId;
      const homePagePropertyId = 'pagePanel.' + pagePaneSpec.menuSecurityId;
      if (webAppSpec.inaccessibleItems.includes(propertyId)) {
        return true;
      }
      return webAppSpec.inaccessibleItems.includes(homePagePropertyId);
    } else {
      return false;
    }
  }

  public isPageMenuItemInaccessible(formLayoutField: LMFormLayoutField | LMFormLayoutMenu): boolean {
    if (!formLayoutField) {
      return false;
    }

    const webAppName = this.lmContextService.getWebApp();
    const webAppSpec = this.webAppMenuSpecs[webAppName];

    if (!webAppSpec) {
      return false;
    }

    if (
      webAppSpec.menuSecurityEnabled &&
        webAppSpec.inaccessibleItems !== undefined &&
        formLayoutField.menuSecurityId !== undefined
    ) {
      const menuName = webAppSpec.menuName;
      // need to keep in sync with SecurityHelper.buildFullPageMenuItemId()
      const propertyId =
          'pageMenuItem.' + menuName + '.' + formLayoutField.menuSecurityId;
      const homePagePropertyId =
          'pageMenuItem.' + formLayoutField.menuSecurityId;
      if (webAppSpec.inaccessibleItems.includes(propertyId)) {
        return true;
      }

      return webAppSpec.inaccessibleItems.includes(homePagePropertyId);
    } else {
      return false;
    }
  }

  public isFormPanelInaccessible(formPanelSpec: LMCompositePanelSpec): boolean {
    const webAppName = this.lmContextService.getWebApp();
    const webAppSpec = this.webAppMenuSpecs[webAppName];

    if (!webAppSpec) {
      return false;
    }

    if (
      webAppSpec.menuSecurityEnabled &&
        webAppSpec.inaccessibleItems !== undefined &&
        formPanelSpec.menuSecurityId !== undefined
    ) {
      const menuName = webAppSpec.menuName;
      // need to keep in sync with SecurityHelper.buildFullFormPanelId()
      const propertyId =
          'formPanel.' + menuName + '.' + formPanelSpec.menuSecurityId;
      return webAppSpec.inaccessibleItems.includes(propertyId);
    } else {
      return false;
    }
  }
}
